import React from 'react';
import { agentAvatar, agentColor, agentId, agentLabel } from '../../agents.js';
import { Stamp } from '../Stamp/Stamp.jsx';
import { fx } from '../../fx.js';
import { BODY, MONO, ink } from '../../primitives.js';

const h = React.createElement;

const ASSETS = null;

const DIZZY = { alf: 'Easy. I keep the calendar, not the carousel.', claw: 'Stack overflow. In my head.', penny: 'That’s five taps. I’m billing you for the sixth.', scout: 'Whoa. Pipeline’s spinning. So am I.', flux: 'Great, now the draft is dizzy too.', none: 'Please stop.' };

/**
 * An agent's portrait tile: the embedded pixel-art portrait (Alf, Claw, Penny, Scout), else the
 * initial on the identity hue (Flux). `presence` dot, `running` RUN tag, tilt on hover when
 * clickable. `assetBase` (or GRYD2.agentAssetBase) overrides the embedded portraits.
 *
 * @param {object} p
 * @param {(line: string) => void} [p.onDizzy]
 * @param {'alf'|'alfred'|'claw'|'main'|'penny'|'scout'|'flux'|string} p.name
 * @param {string} [p.color]
 * @param {number} [p.size]
 * @param {number} [p.radius]
 * @param {number} [p.border]
 * @param {string} [p.title]
 * @param {boolean} [p.dim]
 * @param {'live'|'idle'|'error'|'off'} [p.presence]
 * @param {string} [p.presenceTitle]
 * @param {boolean} [p.running]
 * @param {React.MouseEventHandler} [p.onClick]
 * @param {boolean} [p.tilt]
 * @param {React.CSSProperties} [p.style]
 * @param {string|null} [p.assetBase]
 */
export function AgentTile({ name, color, size = 28, radius, border = 2, title, dim, presence, presenceTitle, running, onClick, tilt = true, style, assetBase = ASSETS, onDizzy }) {
  const [broken, setBroken] = React.useState(false);
  const [dizzy, setDizzy] = React.useState(false);
  const ref = React.useRef(null);
  /* five quick taps: the tile spins dizzy and a coral DIZZY stamp slaps on. onDizzy(line) gets a line in the agent's voice. */
  const tapRef = React.useRef(null);
  if (!tapRef.current) tapRef.current = (fx.taps || (() => () => {}))(5, 1600, () => { fx.wobble && fx.wobble(ref.current); setDizzy(true); setTimeout(() => setDizzy(false), 1500); onDizzy && onDizzy(DIZZY[agentId(name)] || DIZZY.none); });
  const enter = () => { const f = fx; if (f.finePointer && f.finePointer() && !(onClick && tilt)) f.boop(ref.current, .7); };
  const click = (e) => { tapRef.current(); onClick && onClick(e); };
  const src = agentAvatar(name, assetBase);
  const r = radius != null ? radius : Math.round(size * .32);
  const pres = presence === 'live' ? 'var(--lime)' : presence === 'idle' ? 'var(--gold)' : presence === 'error' ? 'var(--coral)' : presence === 'off' ? 'var(--mut3)' : null;
  return h('span', {
    ref, title: title || agentLabel(name), onClick: click, onPointerEnter: enter, className: onClick && tilt ? 'mo-tilt' : undefined, role: onClick ? 'button' : undefined,
    style: { position: 'relative', display: 'inline-grid', placeItems: 'center', width: size, height: size, borderRadius: r, background: color || agentColor(name), color: '#fff', border: ink(border), boxSizing: 'border-box', fontFamily: BODY, fontWeight: 800, fontSize: Math.round(size * .43), opacity: dim ? .5 : 1, cursor: onClick ? 'pointer' : undefined, overflow: 'visible', flex: 'none', ...style }
  },
    src && !broken ? h('img', { src, alt: '', onError: () => setBroken(true), style: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: Math.max(0, r - border), display: 'block' } }) : agentLabel(name)[0],
    pres && !running ? h('span', { title: presenceTitle, style: { position: 'absolute', right: -3, bottom: -3, width: Math.max(8, size * .3), height: Math.max(8, size * .3), borderRadius: '50%', background: pres, border: '2px solid var(--bg)', boxSizing: 'border-box' } }) : null,
    running ? h('span', { title: presenceTitle || 'Running', style: { position: 'absolute', right: -size * .5, top: -size * .32, transform: 'rotate(6deg)', background: 'var(--lime)', color: 'var(--on-accent)', border: ink(1.5), borderRadius: 4, padding: '2px 5px', fontFamily: MONO, fontSize: 8, fontWeight: 700, letterSpacing: '.08em', lineHeight: 1 } }, 'RUN') : null,
    dizzy && Stamp ? h(Stamp, { tone: 'coral', style: { position: 'absolute', left: '50%', top: -Math.max(12, size * .3), marginLeft: -22, fontSize: 8, padding: '3px 5px', zIndex: 2, pointerEvents: 'none' } }, 'Dizzy') : null);
}
